import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PresentationIcon, PlayIcon, FolderIcon, ChevronRightIcon, StarIcon, ClockIcon, MonitorIcon, CastIcon } from 'lucide-react';
const presentations = [{
  id: 1,
  title: 'Q4 2024 Board Presentation',
  category: 'Board Meetings',
  slides: 42,
  lastUsed: '2 days ago',
  thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop',
  starred: true
}, {
  id: 2,
  title: 'Company Vision 2025',
  category: 'Strategy',
  slides: 28,
  lastUsed: '1 week ago',
  thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop',
  starred: true
}, {
  id: 3,
  title: 'Product Roadmap',
  category: 'Product',
  slides: 35,
  lastUsed: '3 days ago',
  thumbnail: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=300&h=200&fit=crop',
  starred: false
}, {
  id: 4,
  title: 'Investor Pitch Deck',
  category: 'Investor Relations',
  slides: 24,
  lastUsed: '5 days ago',
  thumbnail: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=300&h=200&fit=crop',
  starred: true
}, {
  id: 5,
  title: 'Annual Report 2024',
  category: 'Reports',
  slides: 56,
  lastUsed: '1 month ago',
  thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=300&h=200&fit=crop',
  starred: false
}, {
  id: 6,
  title: 'Team Performance Review',
  category: 'HR',
  slides: 18,
  lastUsed: '2 weeks ago',
  thumbnail: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=300&h=200&fit=crop',
  starred: false
}];
const categories = ['All', 'Board Meetings', 'Strategy', 'Product', 'Investor Relations', 'Reports'];
export function PresentationsView() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPresentation, setSelectedPresentation] = useState<number | null>(null);
  const filteredPresentations = selectedCategory === 'All' ? presentations : presentations.filter(p => p.category === selectedCategory);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="glass-card rounded-2xl p-6 h-full flex flex-col" data-id="element-426">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-427">
        <div className="flex items-center gap-3" data-id="element-428">
          <div className="w-10 h-10 rounded-xl bg-[rgba(249,115,22,0.15)] flex items-center justify-center" data-id="element-429">
            <PresentationIcon className="w-5 h-5 text-orange-400" data-id="element-430" />
          </div>
          <div data-id="element-431">
            <h3 className="text-lg font-semibold font-['Playfair_Display'] text-white" data-id="element-432">
              Presentations
            </h3>
            <p className="text-xs text-gray-400" data-id="element-433">
              {presentations.length} presentations available
            </p>
          </div>
        </div>
        <button className="touch-button h-10 px-4 rounded-xl bg-[rgba(201,168,76,0.15)] hover:bg-[rgba(201,168,76,0.25)] text-[#C9A84C] text-sm font-medium transition-colors" data-id="element-434">
          <CastIcon className="w-4 h-4 mr-2" data-id="element-435" />
          Cast to Display
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 -mx-2 px-2" data-id="element-436">
        {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors touch-button h-10 ${selectedCategory === category ? 'bg-[rgba(201,168,76,0.2)] text-[#C9A84C] border border-[rgba(201,168,76,0.3)]' : 'bg-[rgba(255,255,255,0.03)] text-gray-400 hover:bg-[rgba(255,255,255,0.06)]'}`} data-id="element-437">
            {category}
          </button>)}
      </div>

      {/* Presentations Grid */}
      <div className="flex-1 overflow-y-auto" data-id="element-438">
        <div className="grid grid-cols-3 gap-4" data-id="element-439">
          <AnimatePresence mode="popLayout" data-id="element-440">
            {filteredPresentations.map((presentation, index) => <motion.button key={presentation.id} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            delay: 0.05 * index
          }} onClick={() => setSelectedPresentation(presentation.id)} className="group text-left rounded-xl overflow-hidden bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.05)] transition-all touch-button h-auto p-0 flex-col" data-id="element-441">
                {/* Thumbnail */}
                <div className="relative aspect-video" data-id="element-442">
                  <img src={presentation.thumbnail} alt="" className="w-full h-full object-cover" data-id="element-443" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-444" />

                  {/* Play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30" data-id="element-445">
                    <div className="w-14 h-14 rounded-full bg-[#C9A84C] flex items-center justify-center shadow-lg" data-id="element-446">
                      <PlayIcon className="w-6 h-6 text-[#0D0F14] ml-1" data-id="element-447" />
                    </div>
                  </div>

                  {/* Star badge */}
                  {presentation.starred && <div className="absolute top-2 right-2" data-id="element-448">
                      <StarIcon className="w-5 h-5 text-[#C9A84C] fill-[#C9A84C]" data-id="element-449" />
                    </div>}

                  {/* Slide count */}
                  <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/60 text-xs text-white" data-id="element-450">
                    {presentation.slides} slides
                  </div>
                </div>

                {/* Info */}
                <div className="p-3" data-id="element-451">
                  <h4 className="text-sm font-medium text-white group-hover:text-[#C9A84C] transition-colors line-clamp-1" data-id="element-452">
                    {presentation.title}
                  </h4>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500" data-id="element-453">
                    <FolderIcon className="w-3 h-3" data-id="element-454" />
                    <span data-id="element-455">{presentation.category}</span>
                    <span data-id="element-456">•</span>
                    <ClockIcon className="w-3 h-3" data-id="element-457" />
                    <span data-id="element-458">{presentation.lastUsed}</span>
                  </div>
                </div>
              </motion.button>)}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-[rgba(201,168,76,0.1)] flex items-center justify-between" data-id="element-459">
        <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-460">
          <MonitorIcon className="w-4 h-4" data-id="element-461" />
          <span data-id="element-462">Display: Main Conference Screen</span>
        </div>
        <button className="flex items-center gap-1 text-sm text-[#C9A84C] hover:text-[#D4AF37] transition-colors" data-id="element-463">
          Browse All Files
          <ChevronRightIcon className="w-4 h-4" data-id="element-464" />
        </button>
      </div>
    </motion.div>;
}